import { PureComponent, ReactNode } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';

import { ThunkDispatch } from 'redux-thunk';
import { GlobalState } from '../store/reducers';
import { Actions } from '../store/actions';
import fetchMedicalCertificatesByPatientId, {
    ResponseEntity,
} from '../api/fetchMedicalCertificatesByPatientId';
import { PX_PDF_ELEMENT_ID, RIGHT_PANEL_CONC } from './ReceiptsRightPanel';

interface Props {
    dispatch: ThunkDispatch<GlobalState, null, Actions>;
    plId: string;
    medicalCertificateId?: string;
    goToMedicalCertificate: (id: string) => void;
}

type State = {
    fetchState: 'loading' | 'success' | 'error';
    list: ResponseEntity[];
};

class MedicalCertificatesRightPanel extends PureComponent<Props, State> {
    state: State = {
        fetchState: 'loading',
        list: [],
    };

    componentDidMount = async (): Promise<void> => {
        this.fetchMedicalCertificates();
    };

    fetchMedicalCertificates = () => {
        fetchMedicalCertificatesByPatientId(this.props.plId)
            .then(async (res) => {
                if (!res.success) {
                    console.error('No data found');
                    alert('Failed to fetch latest details of the patient [1]');
                    return;
                }

                this.setState({
                    list: res.data || [],
                    fetchState: 'success',
                });

                if (res.data && res.data.length > 0) {
                    if (!this.props.medicalCertificateId) {
                        this.props.goToMedicalCertificate(res.data[0].id);
                    } else {
                        const selectedMedicalCertificate = res.data.find(
                            (r) => r.id === this.props.medicalCertificateId,
                        );

                        if (!selectedMedicalCertificate) {
                            this.props.goToMedicalCertificate(res.data[0].id);
                        }
                    }
                } else {
                    this.props.goToMedicalCertificate('');
                }
            })
            .catch((e) => {
                console.error(e);
                alert('Failed to fetch latest details of the patient  [2]');
            });
    };

    onMedicalCertificateSelect = (id: string): void => {
        this.props.goToMedicalCertificate(id);
    };

    onPrint = async () => {
        const selectedMedicalCertificate = this.state.list.find(
            (r) => r.id === this.props.medicalCertificateId,
        );

        if (!selectedMedicalCertificate) {
            return;
        }

        const url = selectedMedicalCertificate.url;
        window.open(url);
    };

    render(): ReactNode {
        const { list, fetchState } = this.state;

        const { medicalCertificateId } = this.props;

        const selectedMedicalCertificate = list.find((r) => r.id === medicalCertificateId);

        if (fetchState === 'loading') {
            return (
                <div className="flex flex-col col-span-10 items-center justify-center w-full h-full space-y-64">
                    <div className="pulse">
                        <svg width="16px" height="12px">
                            <polyline id="back" points="1 6 4 6 6 11 10 1 12 6 15 6"></polyline>
                            <polyline id="front" points="1 6 4 6 6 11 10 1 12 6 15 6"></polyline>
                        </svg>
                    </div>
                    <p className="text-24 text-neutral-black">Fetching medical certificates...</p>
                </div>
            );
        }

        return (
            <div className="flex col-span-10 overflow-hidden bg-white">
                <div style={{ width: 0, height: 0 }} id={RIGHT_PANEL_CONC}></div>
                <div className="w-30p">
                    <div className="flex flex-col  h-full w-full bg-neutral-9 relative fade-visits">
                        <div className="absolute inset-0 overflow-auto py-16">
                            <div className="space-y-24 p-16 ">
                                {list.length === 0 && (
                                    <div className="space-y-16">
                                        <p className="leading-5 text-12 text-neutral-5 font-700">
                                            {moment().format('MMMM YYYY')}
                                        </p>
                                        <div className="flex flex-col space-y-16">
                                            {Array.from(Array(2)).map((i) => (
                                                <div
                                                    key={i}
                                                    className="flex items-center w-full space-x-16"
                                                >
                                                    <div className="w-20 h-32 text-center font-700 bg-neutral-7"></div>
                                                    <div className="flex items-center justify-between h-32 p-8 leading-6 bg-neutral-7 w-87p rounded-32"></div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {list?.map((item, i) => (
                                    <div key={item.id} className="space-y-16">
                                        {(i === 0 ||
                                            moment(item.created_at).format('MMMM YYYY') !==
                                                moment(list[i - 1].created_at).format(
                                                    'MMMM YYYY',
                                                )) && (
                                            <p className="leading-5 text-12 text-neutral-5 font-700">
                                                {moment(item.created_at).format('MMMM YYYY')}
                                            </p>
                                        )}
                                        <div className="flex flex-col space-y-16">
                                            <div
                                                onClick={() =>
                                                    this.onMedicalCertificateSelect(item.id)
                                                }
                                                className="flex items-center space-x-16 cursor-pointer w-full"
                                            >
                                                <div className="text-center font-700">
                                                    <p
                                                        className={
                                                            medicalCertificateId !== item.id
                                                                ? 'text-18 text-neutral-5'
                                                                : 'text-18 text-neutral-1'
                                                        }
                                                    >
                                                        {new Date(item.created_at).getDate()}
                                                    </p>
                                                    <p className="text-10 text-neutral-5">
                                                        {moment(item.created_at).format('ddd')}
                                                    </p>
                                                </div>
                                                <div
                                                    className={
                                                        medicalCertificateId === item.id
                                                            ? 'flex items-center justify-between w-87p p-8 leading-6 bg-white rounded-32 shadow-tight-3 transform-gpu scale-103'
                                                            : 'flex items-center justify-between w-87p p-8 leading-6 bg-white rounded-32 patient-details-visit-shadow hover:shadow-tight-3 hover:scale-103 transition-all transform-gpu duration-300 ease-in-out'
                                                    }
                                                >
                                                    <div
                                                        className={
                                                            'flex items-center  w-80p capitalize'
                                                        }
                                                    >
                                                        <div
                                                            className={
                                                                medicalCertificateId === item.id
                                                                    ? 'ml-8 text-13 text-neutral-1 font-700 w-85p truncate'
                                                                    : 'ml-8 text-13 text-neutral-5 w-85p truncate'
                                                            }
                                                        >
                                                            {item.name}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <svg
                                                            width="25"
                                                            height="25"
                                                            viewBox="0 0 28 28"
                                                            className={
                                                                medicalCertificateId !== item.id
                                                                    ? 'fill-neutral-1 cursor-pointer fill-neutral-1'
                                                                    : 'fill-neutral-5 cursor-pointer fill-neutral-5'
                                                            }
                                                            preserveAspectRatio="xMidYMid meet"
                                                        >
                                                            <path d="M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z"></path>
                                                        </svg>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                {list.length === 0 ? (
                    <div className="flex flex-1 flex-col items-center justify-center h-full space-y-16 select-none">
                        <div>
                            <svg
                                viewBox="0 0 448 512"
                                width="42"
                                height="48"
                                className="fill-neutral-3"
                            >
                                <path d="M400 64h-48V12c0-6.6-5.4-12-12-12h-8c-6.6 0-12 5.4-12 12v52H128V12c0-6.6-5.4-12-12-12h-8c-6.6 0-12 5.4-12 12v52H48C21.5 64 0 85.5 0 112v352c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48V112c0-26.5-21.5-48-48-48zM48 96h352c8.8 0 16 7.2 16 16v48H32v-48c0-8.8 7.2-16 16-16zm352 384H48c-8.8 0-16-7.2-16-16V192h384v272c0 8.8-7.2 16-16 16zm-105.3-95.9c4.7 4.7 4.7 12.3 0 17l-5.7 5.7c-4.7 4.7-12.3 4.7-17 0l-48-48.2-48.1 48.1c-4.7 4.7-12.3 4.7-17 0l-5.7-5.7c-4.7-4.7-4.7-12.3 0-17l48.1-48.1-48.1-48.1c-4.7-4.7-4.7-12.3 0-17l5.7-5.7c4.7-4.7 12.3-4.7 17 0l48.1 48.1 48.1-48.1c4.7-4.7 12.3-4.7 17 0l5.7 5.7c4.7 4.7 4.7 12.3 0 17L246.6 336l48.1 48.1z"></path>
                            </svg>
                        </div>
                        <div>
                            <p className="text-neutral-3">
                                No medical certificates for this patient yet
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col bg-white w-70p patient-details-viewport-shadow h-full overflow-hidden">
                        <div className="flex flex-col h-full overflow-hidden">
                            <div className="flex flex-col flex-1 overflow-hidden">
                                {selectedMedicalCertificate && (
                                    <iframe
                                        id={PX_PDF_ELEMENT_ID}
                                        className="w-full h-full fade-in"
                                        src={`/pdfjs-3/web/viewer.html?file=${selectedMedicalCertificate?.url}`}
                                    ></iframe>
                                )}
                            </div>
                            {selectedMedicalCertificate && (
                                <div
                                    key={selectedMedicalCertificate.id}
                                    className="grid grid-rows-2 xl:grid-rows-1 gap-16 p-16 grid-flow-col  justify-between patient-details-visit-footer"
                                >
                                    <div className="w-full">
                                        <button
                                            onClick={this.onPrint}
                                            className="secondary-btn justify-center btn px-12 py-8 btn--small w-full"
                                        >
                                            <span className="pr-8 ">
                                                <svg
                                                    width="16"
                                                    height="16"
                                                    viewBox="0 0 28 28"
                                                    className="fill-actionPrimary relative top-2 mr-4 cursor-pointer"
                                                    preserveAspectRatio="xMidYMid meet"
                                                >
                                                    <path d="M5,20H19V18H5M19,9H15V3H9V9H5L12,16L19,9Z"></path>
                                                </svg>
                                            </span>
                                            <span className="whitespace-nowrap text-center">
                                                Download
                                            </span>
                                        </button>
                                    </div>

                                    <div className="flex space-x-8">
                                        <div className="w-full">
                                            <button
                                                onClick={this.onPrint}
                                                className="primary-btn btn px-12 py-8 btn--small w-full justify-center"
                                            >
                                                <span className="pr-8 ">
                                                    <svg
                                                        width="20"
                                                        height="20"
                                                        viewBox="0 0 28 28"
                                                        className="cursor-pointer fill-white"
                                                        preserveAspectRatio="xMidYMid meet"
                                                    >
                                                        <path d="M18,3H6V7H18M19,12A1,1 0 0,1 18,11A1,1 0 0,1 19,10A1,1 0 0,1 20,11A1,1 0 0,1 19,12M16,19H8V14H16M19,8H5A3,3 0 0,0 2,11V17H6V21H18V17H22V11A3,3 0 0,0 19,8Z"></path>
                                                    </svg>
                                                </span>
                                                <span className=" text-center">Print</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export default connect()(MedicalCertificatesRightPanel);
